import { isFeatureEnabled } from 'owa-feature-flags';
import isImageProxyEnabled from 'owa-inline-image-loader/lib/utils/isImageProxyEnabled';
import BodyType from 'owa-service/lib/contract/BodyType';
import ExtendedPropertyUri from 'owa-service/lib/contract/ExtendedPropertyUri';
import ItemResponseShape from 'owa-service/lib/contract/ItemResponseShape';
import extendedPropertyUri from 'owa-service/lib/factory/extendedPropertyUri';
import itemResponseShape from 'owa-service/lib/factory/itemResponseShape';
import propertyUri from 'owa-service/lib/factory/propertyUri';
import { getExtendedPropertyUri } from 'owa-service/lib/Utils';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    INLINEIMAGE_CUSTOM_DATA_TEMPLATE,
    INLINEIMAGE_URL_TEMPLATE,
} from 'owa-inline-image-consts';

/**
 * Per definition in substrate: /sources/dev/services/src/Services/Core/Types/ImageProxyCapability.cs
 */
enum ImageProxyCapability {
    None = 'None',
    OwaProxy = 'OwaProxy',
    ConnectorsProxy = 'ConnectorsProxy',
    Compose = 'Compose',
    OwaAndConnectorsProxy = 'OwaAndConnectorsProxy',
    OwaConnectorsProxyAndCompose = 'OwaConnectorsProxyAndCompose',
}

export const cssScopeClassName = 'rps_' + getRandomBetween(1, 0xffff).toString(16);
export const maximumBodySize = 2 * 1024 * 1024;
export const maximumRecipientsToReturn = 20;

const EXPLICIT_MESSAGE_CARD_PROPERTY_NAME = 'ExplicitMessageCard';
const COMMON_PROPERTY_SET_GUID = '00062008-0000-0000-C000-000000000046';

function getRandomBetween(min: number, max: number): number {
    return Math.ceil(Math.random() * (max - min) + min);
}

function getBlockContentFromUnknownSenders() {
    const userConfiguration = getUserConfiguration();
    return userConfiguration.SafetyUserOptions
        ? userConfiguration.SafetyUserOptions.BlockContentFromUnknownSenders
        : false;
}

export function getComposeItemResponseShape(bodyType: BodyType) {
    const shape = itemResponseShape({
        BaseShape: 'IdOnly',
        ClientSupportsIrm: true,
        FilterHtmlContent: true,
        InlineImageCustomDataTemplate: INLINEIMAGE_CUSTOM_DATA_TEMPLATE,
        // Bug 25298: Compose send may accidentally include inline image place holder
        // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/25298
        // ComposeItemResponseShape is used for requesting item to be loaded in compose, i.e. draft
        // In the past, we trigger inline image load through injecting client side javascript reference
        // "onload=InlineImageLoader.GetLoader().Load(this)" just like reading pane. This causes a place holder
        // to be displayed and may accidently be sent out if users send too fast before image loads
        // The fix is to not to rely on this script injection, but to hook on editor ContentChange event
        // and fire up inline image load programatically where we can control using of place holder
        InlineImageUrlOnLoadTemplate: '',
        InlineImageUrlTemplate: INLINEIMAGE_URL_TEMPLATE,
        AdditionalProperties: [propertyUri({ FieldURI: 'ItemLastModifiedTime' })],
    });

    configItemResponseShapeForCLP(shape);

    if (bodyType) {
        shape.BodyType = bodyType;
    }

    // When image proxy is disabled, send a 'None' to receive zero change to external images from server side
    shape.ImageProxyCapability =
        isFeatureEnabled('rp-replyImageProxy') && isImageProxyEnabled()
            ? ImageProxyCapability.OwaConnectorsProxyAndCompose
            : ImageProxyCapability.None;

    return shape;
}

export function getBaseItemResponseShape(): ItemResponseShape {
    const shape = itemResponseShape({
        BaseShape: 'IdOnly',
        AddBlankTargetToLinks: true,
        BlockContentFromUnknownSenders: getBlockContentFromUnknownSenders(),
        BlockExternalImagesIfSenderUntrusted: true,
        ClientSupportsIrm: true,
        CssScopeClassName: cssScopeClassName,
        FilterHtmlContent: true,
        FilterInlineSafetyTips: true,
        InlineImageCustomDataTemplate: INLINEIMAGE_CUSTOM_DATA_TEMPLATE,
        InlineImageUrlTemplate: INLINEIMAGE_URL_TEMPLATE,
        MaximumBodySize: maximumBodySize,
        MaximumRecipientsToReturn: maximumRecipientsToReturn,
        ImageProxyCapability: isImageProxyEnabled()
            ? ImageProxyCapability.OwaAndConnectorsProxy
            : ImageProxyCapability.None,
        AdditionalProperties: [
            propertyUri({ FieldURI: 'CanDelete' }),
            extendedPropertyUri(getExtendedPropertyUriForExplicitMessageCard()),
        ],
    });

    // Bug 42744: fwk-enforceCsp disable inline code which also disables inline image load
    // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/42744
    // To workaround CSP disabling inline code, we will use a InlineImageHandler (a content handler) that runs
    // after content is loaded in reading pane
    shape.InlineImageUrlOnLoadTemplate = '';

    return shape;
}

/**
 * The response shape used for conversation is pretty much same as the one for item with
 * some additional conversation only flags
 */
export function getConversationItemResponseShape(): ItemResponseShape {
    const convItemResponseShape = getBaseItemResponseShape();
    convItemResponseShape.ExcludeBindForInlineAttachments = true;
    convItemResponseShape.CalculateOnlyFirstBody = true;
    return convItemResponseShape;
}

export function configItemResponseShapeForCLP(shape: ItemResponseShape): void {
    if (isFeatureEnabled('cmp-clp')) {
        shape.AdditionalProperties.push(
            extendedPropertyUri({
                PropertyName: 'msip_labels',
                DistinguishedPropertySetId: 'InternetHeaders',
                PropertyType: 'String',
            })
        );
    }
}

export function configItemResponseShapeForRevocation(shape: ItemResponseShape): void {
    if (isFeatureEnabled('rp-omeRevocation')) {
        shape.AdditionalProperties.push(
            extendedPropertyUri({
                PropertyName: 'x-ms-exchange-organization-ome-messagestate',
                DistinguishedPropertySetId: 'InternetHeaders',
                PropertyType: 'String',
            })
        );
    }
}

function getExtendedPropertyUriForExplicitMessageCard(): ExtendedPropertyUri {
    return getExtendedPropertyUri(
        COMMON_PROPERTY_SET_GUID,
        EXPLICIT_MESSAGE_CARD_PROPERTY_NAME,
        'String'
    );
}
